/**
 * The typed HTTP client (TICKET FE-003).
 *
 * Everything the app knows about the backend goes through here: the token, the
 * correlation id, and the shape of an error. A component never calls `fetch`.
 */

export const API_BASE = "/api/v1";

/** Where the access token lives between reloads. */
const TOKEN_STORAGE_KEY = "hdv.access_token";

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function loadStoredToken(): string | null {
  if (accessToken) return accessToken;
  try {
    accessToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // Private browsing or blocked storage: the session simply will not survive
    // a reload, which is a nuisance and not a failure.
    accessToken = null;
  }
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* see loadStoredToken */
  }
}

/** Called when the server says the session is over, so the app can show login. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** A structured error from the API, carrying the id needed to find it in the logs. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly correlationId: string | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
    correlationId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.correlationId = correlationId;
  }

  /** True when retrying could plausibly help. */
  get isTransient(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown>; correlation_id?: string };
  detail?: unknown;
}

export function newCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `hdv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function toApiError(response: Response): Promise<ApiError> {
  const correlationId = response.headers.get("X-Correlation-ID");
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    /* an empty or non-JSON body is still an error we can report */
  }

  if (body.error) {
    return new ApiError(
      response.status,
      body.error.code ?? "error",
      body.error.message ?? response.statusText,
      body.error.details ?? {},
      body.error.correlation_id ?? correlationId,
    );
  }

  // FastAPI's own 422 shape: a list of field problems.
  if (Array.isArray(body.detail)) {
    const first = body.detail[0] as { msg?: string; loc?: unknown[] } | undefined;
    const field = Array.isArray(first?.loc) ? first?.loc.slice(1).join(".") : "";
    const message = first?.msg ?? "Requête invalide.";
    return new ApiError(
      response.status,
      "validation_error",
      field ? `${field} : ${message}` : message,
      { detail: body.detail },
      correlationId,
    );
  }

  return new ApiError(
    response.status,
    "error",
    typeof body.detail === "string" ? body.detail : response.statusText || "Erreur réseau",
    {},
    correlationId,
  );
}

/**
 * Raised when a request could not be sent and was kept for later.
 *
 * Not an error in the sense the operator cares about: the saisie is safe. The
 * mutation hooks surface it as "en attente de sync", never as a failure.
 */
export class QueuedOfflineError extends Error {
  readonly entryId: number;

  constructor(entryId: number, label: string) {
    super(`Enregistré hors ligne : ${label}`);
    this.name = "QueuedOfflineError";
    this.entryId = entryId;
  }
}

/** Set by the app so the client can queue without importing the outbox. */
let enqueueOffline:
  | ((request: {
      label: string;
      path: string;
      body?: unknown;
      file?: { blob: Blob; name: string; field: string };
      idempotencyKey: string;
    }) => Promise<{ id: number }>)
  | null = null;

export function setOfflineQueue(handler: typeof enqueueOffline): void {
  enqueueOffline = handler;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** Sent as multipart instead of JSON. */
  formData?: FormData;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  /** Skip the Authorization header (login only). */
  anonymous?: boolean;
  /**
   * Keep this operation for later if the network is gone.
   *
   * Only ever set on a request that carries an idempotency key: replaying a
   * queue means sending the same thing twice without knowing whether the first
   * one arrived, and the key is what makes that safe (ADR 0009).
   */
  offline?: { label: string; idempotencyKey: string };
  /** The photo behind a multipart upload, kept so a queued one can be rebuilt. */
  offlineFile?: { blob: Blob; name: string; field: string };
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Perform one API call.
 *
 * Retries are deliberately timid: only idempotent GETs, only on a network
 * failure or a 5xx, twice at most. Retrying a POST could post a movement twice —
 * that is what idempotency keys are for, and only the caller knows the key.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, formData, query, signal, anonymous = false, offline } = options;
  const url = buildUrl(path, query);
  const maxAttempts = method === "GET" ? 3 : 1;

  let lastError: ApiError | null = null;
  // A FormData cannot be replayed from itself once consumed, so a queued upload
  // keeps the blob it was built from.
  const pendingFile = options.offlineFile;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const headers: Record<string, string> = { "X-Correlation-ID": newCorrelationId() };
    if (!anonymous) {
      const token = loadStoredToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    if (body !== undefined && !formData) headers["Content-Type"] = "application/json";

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
        signal,
      });
    } catch (cause) {
      if ((cause as Error)?.name === "AbortError") throw cause;
      lastError = new ApiError(0, "network_error", "Impossible de joindre HD-Vault.");
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
        continue;
      }
      // The network is gone and this operation said it could wait. Keep it
      // rather than losing what someone just counted (Frontend Spec §25).
      if (offline && enqueueOffline) {
        const queued = await enqueueOffline({
          label: offline.label,
          path,
          body,
          file: pendingFile,
          idempotencyKey: offline.idempotencyKey,
        });
        throw new QueuedOfflineError(queued.id, offline.label);
      }
      throw lastError;
    }

    if (response.ok) {
      if (response.status === 204) return undefined as T;
      const text = await response.text();
      return (text ? JSON.parse(text) : undefined) as T;
    }

    const error = await toApiError(response);
    if (error.status === 401 && !anonymous) {
      setAccessToken(null);
      onUnauthorized?.();
      throw error;
    }
    if (error.isTransient && attempt < maxAttempts) {
      lastError = error;
      await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
      continue;
    }
    throw error;
  }

  throw lastError ?? new ApiError(0, "error", "Erreur inconnue.");
}

/** URL of a stored file. Used directly in `<img src>`; the browser sends no token,
 *  so media reads rely on the same-origin session cookie once one exists. Until
 *  then the gallery fetches through {@link fetchMediaObjectUrl}. */
export function mediaContentPath(mediaId: string): string {
  return `${API_BASE}/media/${mediaId}/content`;
}

/**
 * Fetch a stored image with the Authorization header and return an object URL.
 *
 * `<img src>` cannot carry a bearer token, and media is behind auth on purpose
 * (MinIO is not reachable from outside). The caller must revoke the URL.
 */
export async function fetchMediaObjectUrl(mediaId: string, signal?: AbortSignal): Promise<string> {
  const token = loadStoredToken();
  const response = await fetch(mediaContentPath(mediaId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  if (!response.ok) throw await toApiError(response);
  return URL.createObjectURL(await response.blob());
}
